/**
 * Public analytics ingestion schemas — shared between the API
 * (`POST /api/analytics/event`) and the eventual client beacon. Issue #22.
 *
 * Spec references:
 *   - §14 Event Types — the full `AnalyticsEventType` enum is replicated here
 *     so the schema is the single source of truth for both the wire shape and
 *     the Prisma column.
 *   - §15 Bot Detection Signals — bot classification is metadata on every
 *     event, never an event type, so the schema does not include
 *     `isBot/botScore/botType`. Those are populated server-side from
 *     `botClassify`.
 *   - §20 Schema Versioning — `metadataJson` carries `schemaVersion: 1`.
 *
 * Coordination with #20 (Dev A's leads work):
 *   - Both endpoints accept a shared `visitorId` (the browser's `op_visitor_id`
 *     localStorage key). Keep the convention identical so a visitor's lead and
 *     analytics events tie together via the same id.
 */

import { z } from "zod";
import { analyticsEventMetadataV1 } from "./json.js";

/**
 * The full event-type enum (mirrors `AnalyticsEventType` in `schema.prisma`).
 * Kept as a const tuple so Zod can produce a concrete enum + the static type
 * is exact (not widened to `string`).
 */
export const ANALYTICS_EVENT_TYPES = [
  "PAGE_VIEW",
  "SESSION_START",
  "SESSION_END",
  "CTA_CLICK",
  "CONTACT_FORM_VIEW",
  "CONTACT_FORM_SUBMIT",
  "BLOG_VIEW",
  "PRODUCT_VIEW",
  "EXPERIMENT_VIEW",
  "EXPERIMENT_CONVERSION",
  "OUTBOUND_LINK_CLICK",
  "FILE_DOWNLOAD",
] as const;

export const analyticsEventTypeSchema = z.enum(ANALYTICS_EVENT_TYPES);
export type AnalyticsEventTypeWire = z.infer<typeof analyticsEventTypeSchema>;

/**
 * Body schema for `POST /api/analytics/event`.
 *
 * `.strict()` rejects unknown top-level keys so a typo on `eventType` /
 * `visitorId` etc. surfaces as a 422 instead of being silently dropped.
 *
 * Field rules:
 *   - `siteId` — required; resolved against the current host's Site in the
 *     service so a malicious caller can't write events into someone else's
 *     site even with a valid id (defence in depth: integration tests rely on
 *     the host-scoped check).
 *   - `eventType` — strict enum match. Bad values → 422.
 *   - `path` — request-relative path the event happened on. Bounded to 2048
 *     chars so a hostile caller can't bloat the JSONB column with a 1-MB
 *     query string.
 *   - `visitorId` / `sessionId` — opaque client-supplied ids; bounded length
 *     to keep individual rows compact.
 *   - `referrer` — bounded; we don't validate URL shape because referrers in
 *     the wild include all manner of malformed strings (mobile app deep-link
 *     handlers, etc.) and we'd rather record them than 422.
 *   - `metadata` — passes through `analyticsEventMetadataV1`. Unknown
 *     `schemaVersion` → 422 (the schema's `z.literal(1)` enforces this).
 */
export const analyticsEventInputSchema = z
  .object({
    siteId: z.string().min(1).max(64),
    eventType: analyticsEventTypeSchema,
    path: z.string().min(1).max(2048),
    visitorId: z.string().min(1).max(128).optional(),
    sessionId: z.string().min(1).max(128).optional(),
    referrer: z.string().max(2048).optional(),
    metadata: analyticsEventMetadataV1.optional(),
  })
  .strict();
export type AnalyticsEventInput = z.infer<typeof analyticsEventInputSchema>;
